// src/components/KeyValuesDashboard/KeyValuesDashboard.tsx

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Filter,
  RefreshCw,
  AlertCircle,
  Layers,
} from 'lucide-react';

import { compareValues, getAvailableOptions } from '@/services/api';
import type { ComparisonRequest } from '@/types/analytics';
import { useAoTrocarConta } from '@/contexts/NetworkContext';

// Components
import { KeyValueFilters } from './KeyValueFilters';
import { KeyValueTabs } from './KeyValueTabs';
import { LoadingState } from './LoadingEmptyStates';
import { EmptyState } from './EmptyState';

const CACHE_KEYS = {
  START_DATE: 'kv_start_date',
  END_DATE: 'kv_end_date',
  DOMAINS: 'kv_domains',
  SELECTED_KEY: 'kv_selected_key',
  CUSTOM_VALUES: 'kv_custom_values',
  MODE: 'kv_mode',
};

const getYesterday = () => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return yesterday.toISOString().split('T')[0];
};

interface KeyValuesDashboardProps {
  onBack: () => void;
}

export function KeyValueDashboard({ onBack }: KeyValuesDashboardProps) {
  // ============================================================
  // ESTADOS BASE
  // ============================================================
  const [startDate, setStartDate] = useState(() => 
    localStorage.getItem(CACHE_KEYS.START_DATE) || getYesterday()
  );
  
  const [endDate, setEndDate] = useState(() => 
    localStorage.getItem(CACHE_KEYS.END_DATE) || getYesterday()
  );
  
  const [selectedDomains, setSelectedDomains] = useState<string[]>(() => {
    const cached = localStorage.getItem(CACHE_KEYS.DOMAINS);
    return cached ? JSON.parse(cached) : [];
  });
  // Domínios selecionados são da conta anterior: trocar de conta zera o filtro.
  useAoTrocarConta(() => setSelectedDomains([]));

  const [selectedKey, setSelectedKey] = useState(() =>
    localStorage.getItem(CACHE_KEYS.SELECTED_KEY) || ''
  );
  
  const [selectedValues, setSelectedValues] = useState<string[]>(() => {
    const cached = localStorage.getItem(CACHE_KEYS.CUSTOM_VALUES);
    return cached ? JSON.parse(cached) : [];
  });
  
  // Modo: 'all' = todos os valores | 'compare' = comparar valores específicos
  const [mode, setMode] = useState<'all' | 'compare'>(() => 
    (localStorage.getItem(CACHE_KEYS.MODE) as 'all' | 'compare') || 'all'
  );
  
  // Filtros adicionais
  const [filterCountry, setFilterCountry] = useState('');
  const [filterAdUnit, setFilterAdUnit] = useState('');
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  // ============================================================
  // CACHE LOCALSTORAGE
  // ============================================================
  useEffect(() => localStorage.setItem(CACHE_KEYS.START_DATE, startDate), [startDate]);
  useEffect(() => localStorage.setItem(CACHE_KEYS.END_DATE, endDate), [endDate]);
  useEffect(() => localStorage.setItem(CACHE_KEYS.DOMAINS, JSON.stringify(selectedDomains)), [selectedDomains]);
  useEffect(() => localStorage.setItem(CACHE_KEYS.SELECTED_KEY, selectedKey), [selectedKey]);
  useEffect(() => localStorage.setItem(CACHE_KEYS.CUSTOM_VALUES, JSON.stringify(selectedValues)), [selectedValues]);
  useEffect(() => localStorage.setItem(CACHE_KEYS.MODE, mode), [mode]);

  // ============================================================
  // QUERIES
  // ============================================================
  
  // Buscar opções disponíveis
  const {
    data: availableOptions,
    isLoading: isLoadingOptions,
  } = useQuery({
    queryKey: ['kv_available_options', startDate, endDate, selectedDomains],
    queryFn: () => getAvailableOptions(
      startDate,
      endDate,
      selectedDomains.length > 0 ? selectedDomains : undefined
    ),
  });

  // Buscar comparação
  const {
    data: comparisonData,
    isLoading: isLoadingComparison,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      'kv_comparison',
      startDate,
      endDate,
      selectedDomains,
      selectedKey,
      selectedValues,
      mode,
      filterCountry,
      filterAdUnit,
    ],
    queryFn: () => {
      const request: ComparisonRequest = {
        start: startDate,
        end: endDate,
        custom_key: selectedKey,
        domains: selectedDomains.length > 0 ? selectedDomains : undefined,
        compare_to_previous: true,
        comparison_type: 'previous_period',
        include_daily_breakdown: true,
        include_country_breakdown: true,
        include_ad_unit_breakdown: true,
        include_insights: true,
        alert_threshold: 15.0,
      };

      // Se modo 'compare' e tem valores selecionados, adiciona custom_values
      if (mode === 'compare' && selectedValues.length > 0) {
        request.custom_values = selectedValues;
      }

      // Filtros opcionais
      if (filterCountry) request.country = filterCountry;
      if (filterAdUnit) request.ad_unit_name = filterAdUnit;

      return compareValues(request);
    },
    enabled: !!selectedKey && (
      mode === 'all' || 
      (mode === 'compare' && selectedValues.length > 0)
    ),
    retry: 2,
  });

  // ============================================================
  // HANDLERS
  // ============================================================
  
  const handleDateChange = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
  };

  const handleKeyChange = (key: string) => {
    setSelectedKey(key);
    setSelectedValues([]);
    setMode('all');
  };

  const handleModeChange = (newMode: 'all' | 'compare') => {
    setMode(newMode);
    if (newMode === 'all') {
      setSelectedValues([]);
    }
  };

  const handleValuesChange = (values: string[]) => {
    setSelectedValues(values);
  };

  const handleRefresh = () => {
    refetch();
  };

  // ============================================================
  // VARIÁVEIS DERIVADAS
  // ============================================================
  
  const isLoading = isLoadingOptions || isLoadingComparison;
  const hasData = comparisonData && !isLoading;
  
  const availableKeys = availableOptions?.options?.keys || [];
  const availableValuesForKey = availableKeys.find(
    k => k.custom_key === selectedKey
  )?.values || [];
  
  const canShowData = selectedKey && (
    mode === 'all' || 
    (mode === 'compare' && selectedValues.length > 0)
  );

  // ============================================================
  // RENDER
  // ============================================================
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100">
      <div className="max-w-[1800px] mx-auto p-4 lg:p-6 space-y-6">
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2.5 text-slate-600 hover:text-slate-900 hover:bg-white/80 rounded-xl transition-all shadow-sm hover:shadow-md"
              title="Voltar"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>

            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg">
                  <Layers className="w-6 h-6 text-white" />
                </div>
                Análise de UTM Key-Values
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                Comparação multidimensional com visualizações avançadas
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="p-2.5 text-slate-600 hover:text-slate-900 hover:bg-white/80 rounded-xl transition-all shadow-sm hover:shadow-md disabled:opacity-50"
              title="Atualizar"
            >
              <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>

            <button
              onClick={() => setIsFiltersOpen(true)}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-slate-700 bg-white/80 rounded-xl hover:bg-white transition-all shadow-sm hover:shadow-md"
            >
              <Filter className="w-4 h-4" />
              <span className="hidden sm:inline">Filtros</span>
            </button>
          </div>
        </div>

        {/* Filtros - Card Principal */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden">
          <KeyValueFilters
            startDate={startDate}
            endDate={endDate}
            onDateChange={handleDateChange}
            selectedDomains={selectedDomains}
            onDomainsChange={setSelectedDomains}
            selectedKey={selectedKey}
            onKeyChange={handleKeyChange}
            availableKeys={availableKeys}
            mode={mode}
            onModeChange={handleModeChange}
            selectedValues={selectedValues}
            onValuesChange={handleValuesChange}
            availableValues={availableValuesForKey}
            isLoadingOptions={isLoadingOptions}
          />
        </div>

        {/* Aviso de seleção incompleta */}
        {!canShowData && selectedKey && mode === 'compare' && selectedValues.length === 0 && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200/50 rounded-2xl p-6 shadow-sm">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-amber-900 mb-1">
                  Selecione valores para comparar
                </h3>
                <p className="text-sm text-amber-700">
                  No modo de comparação, selecione pelo menos 2 valores para visualizar os dados.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {canShowData && isLoading && <LoadingState mode={mode} />}

        {/* Error State */}
        {canShowData && isError && (
          <div className="bg-gradient-to-r from-rose-50 to-red-50 border-2 border-rose-200/50 rounded-2xl p-6 shadow-sm">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-rose-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-rose-900 mb-1">
                  Erro ao carregar dados
                </h3>
                <p className="text-sm text-rose-700">
                  {error instanceof Error ? error.message : 'Ocorreu um erro ao buscar os dados.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Conteúdo Principal - Tabs */}
        {canShowData && hasData && (
          <KeyValueTabs
            data={comparisonData}
            mode={mode}
            selectedKey={selectedKey}
            isLoading={isLoading}
          />
        )}

        {/* Empty State - Nenhuma key selecionada */}
        {!selectedKey && (
          <EmptyState
            title="Selecione uma UTM Key para começar"
            description="Escolha uma custom_key acima para visualizar análises detalhadas e comparações."
          />
        )}
      </div>

      {/* Modal de Filtros Avançados */}
      {isFiltersOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-900">Filtros Avançados</h2>
                <button
                  onClick={() => setIsFiltersOpen(false)}
                  className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Filtro por País */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">
                  Filtrar por País
                </label>
                <select
                  value={filterCountry}
                  onChange={(e) => setFilterCountry(e.target.value)}
                  className="w-full px-4 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                >
                  <option value="">Todos os países</option>
                  {availableOptions?.options?.countries?.map((country) => (
                    <option key={country.country} value={country.country}>
                      {country.country} ({country.total_revenue.toFixed(2)} revenue)
                    </option>
                  ))}
                </select>
              </div>

              {/* Filtro por Ad Unit */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">
                  Filtrar por Ad Unit
                </label>
                <select
                  value={filterAdUnit}
                  onChange={(e) => setFilterAdUnit(e.target.value)}
                  className="w-full px-4 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                >
                  <option value="">Todos os ad units</option>
                  {availableOptions?.options?.ad_units?.map((adUnit) => (
                    <option key={adUnit.ad_unit_name} value={adUnit.ad_unit_name}>
                      {adUnit.ad_unit_name} ({adUnit.total_revenue.toFixed(2)} revenue)
                    </option>
                  ))}
                </select>
              </div>

              {/* Botões de ação */}
              <div className="flex items-center gap-3 pt-4">
                <button
                  onClick={() => {
                    setFilterCountry('');
                    setFilterAdUnit('');
                  }}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                >
                  Limpar Filtros
                </button>
                <button
                  onClick={() => setIsFiltersOpen(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-all shadow-md"
                >
                  Aplicar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
